// F3 performance HUD (M10). Live draw calls, fps, streaming state, instance
// counts and a VRAM estimate — the numbers PERF_BUDGET.md budgets against.
// window.__perf.stats() is the hook the benchmark harness (tools/mapbench.mjs)
// reads.
//
// ROUND-9 MADE THIS PANEL A LIAR, so it now measures four things it used to be
// blind to:
//   1. The RENDER-TARGET PASSES. Walking enabled meshes once describes the
//      camera pass and nothing else, but round-9 added three shadow cascades
//      and a contact-shadow prepass on top of the AO depth pass and the
//      survivor's own key map — six passes whose render lists carry terrain
//      tiles and veg batches. Their cost is amortised by the stride each pass
//      is actually serviced at (see _passStride: the strides are NOT ours to
//      declare, they belong to sunshadow.js).
//   2. TEXTURE SIZE BY TYPE. w*h*4*1.33 is 4x low on the float shadow maps and
//      4x high on a single-channel R8 target, and it never counted a
//      depth-stencil attachment at all.
//   3. GEOMETRY. Vertex and index buffers are VRAM too, and at MAP10K scale
//      they are not a rounding error.
//   4. DECODED AUDIO. AudioBuffers live outside performance.memory, so the
//      panel could show the game inside its heap budget while the Sfx cache
//      alone held hundreds of MB of 32-bit float PCM.
import { SceneInstrumentation } from '@babylonjs/core/Instrumentation/sceneInstrumentation';
import { Constants } from '@babylonjs/core/Engines/constants';

// --- texture sizing --------------------------------------------------------
// Bytes per CHANNEL for the types whose channels are stored separately.
const TYPE_BYTES = {
  [Constants.TEXTURETYPE_UNSIGNED_BYTE]: 1,
  [Constants.TEXTURETYPE_BYTE]: 1,
  [Constants.TEXTURETYPE_SHORT]: 2,
  [Constants.TEXTURETYPE_UNSIGNED_SHORT]: 2,
  [Constants.TEXTURETYPE_HALF_FLOAT]: 2,
  [Constants.TEXTURETYPE_FLOAT]: 4,
  [Constants.TEXTURETYPE_INT]: 4,
  [Constants.TEXTURETYPE_UNSIGNED_INTEGER]: 4,
};
// ...and the whole-texel size of the PACKED types, where the channel count is
// already baked into the type and must not be multiplied in again.
const PACKED_TYPE_BYTES = {
  [Constants.TEXTURETYPE_UNSIGNED_SHORT_4_4_4_4]: 2,
  [Constants.TEXTURETYPE_UNSIGNED_SHORT_5_5_5_1]: 2,
  [Constants.TEXTURETYPE_UNSIGNED_SHORT_5_6_5]: 2,
  [Constants.TEXTURETYPE_UNSIGNED_INT_2_10_10_10_REV]: 4,
  [Constants.TEXTURETYPE_UNSIGNED_INT_24_8]: 4,
  [Constants.TEXTURETYPE_UNSIGNED_INT_10F_11F_11F_REV]: 4,
  [Constants.TEXTURETYPE_UNSIGNED_INT_5_9_9_9_REV]: 4,
  [Constants.TEXTURETYPE_FLOAT_32_UNSIGNED_INT_24_8_REV]: 8,
};
// Channels per colour format. RGB is counted as FOUR on purpose: every GL
// driver this game runs on pads a 3-channel texture out to 4 bytes per texel,
// so believing the declared 3 would under-report every opaque albedo we own.
const FORMAT_CHANNELS = {
  [Constants.TEXTUREFORMAT_ALPHA]: 1,
  [Constants.TEXTUREFORMAT_LUMINANCE]: 1,
  [Constants.TEXTUREFORMAT_LUMINANCE_ALPHA]: 2,
  [Constants.TEXTUREFORMAT_RGB]: 4,
  [Constants.TEXTUREFORMAT_RGBA]: 4,
  [Constants.TEXTUREFORMAT_RED]: 1,
  [Constants.TEXTUREFORMAT_RG]: 2,
  [Constants.TEXTUREFORMAT_RED_INTEGER]: 1,
  [Constants.TEXTUREFORMAT_RG_INTEGER]: 2,
  [Constants.TEXTUREFORMAT_RGB_INTEGER]: 4,
  [Constants.TEXTUREFORMAT_RGBA_INTEGER]: 4,
  [Constants.TEXTUREFORMAT_BGRA]: 4,
};
// Depth/stencil formats carry their own whole-texel size; the type field on a
// depth attachment is not a per-channel size.
const DEPTH_FORMAT_BYTES = {
  [Constants.TEXTUREFORMAT_DEPTH16]: 2,
  [Constants.TEXTUREFORMAT_DEPTH24]: 4,          // DEPTH_COMPONENT24 is padded
  [Constants.TEXTUREFORMAT_DEPTH24_STENCIL8]: 4,
  [Constants.TEXTUREFORMAT_DEPTH24UNORM_STENCIL8]: 4,
  [Constants.TEXTUREFORMAT_DEPTH32_FLOAT]: 4,
  [Constants.TEXTUREFORMAT_DEPTH32FLOAT_STENCIL8]: 8,
  [Constants.TEXTUREFORMAT_STENCIL8]: 1,
};
// A depth buffer Babylon generated for us as a plain renderbuffer: WebGL2 gives
// DEPTH_COMPONENT24 / DEPTH24_STENCIL8, both 4 bytes per texel.
const GENERATED_DEPTH_BYTES = 4;
// Mip chain tail: 1 + 1/4 + 1/16 + ... = 4/3, and it is only paid by textures
// that actually have mips. The round-9 render targets do not (NEAREST, no
// mipmaps), which is half of why the old flat 1.33 was wrong about them.
const MIP_TAIL = 4 / 3;

// Bytes per texel for one (type, format) pair.
function texelBytes(type, format) {
  const depth = DEPTH_FORMAT_BYTES[format];
  if (depth) return depth;
  const packed = PACKED_TYPE_BYTES[type];
  if (packed) return packed;
  const perChannel = TYPE_BYTES[type];
  const channels = FORMAT_CHANNELS[format];
  // Unknown (compressed, or a format this Babylon knows and we do not): fall
  // back to the old pessimistic RGBA8 guess rather than silently reporting 0.
  if (perChannel === undefined || channels === undefined) return 4;
  return perChannel * channels;
}

// Full allocation of one scene texture, including cube faces, array layers, the
// mip tail and any depth-stencil attachment hanging off a render target.
function textureBytes(t) {
  const s = t.getSize?.();
  if (!s?.width || !s?.height) return 0;
  const it = t.getInternalTexture?.() || null;
  const type = it ? it.type : t.textureType;
  const format = it ? it.format : t.textureFormat;
  const layers = Math.max(1, it?.depth || 1);
  const faces = t.isCube ? 6 : 1;
  let bytes = s.width * s.height * layers * faces * texelBytes(type, format);
  if (it?.useMipMaps) bytes *= MIP_TAIL;

  // Depth-stencil. scene.textures never lists these — they belong to the
  // render target, not the scene — so a panel that only walks scene.textures
  // misses a 2048x2048 depth buffer per cascade.
  const ds = t.depthStencilTexture;
  if (ds) {
    bytes += s.width * s.height * faces * texelBytes(ds.type, ds.format);
  } else if (t.renderTargetOptions?.generateDepthBuffer) {
    bytes += s.width * s.height * faces * GENERATED_DEPTH_BYTES;
  }
  return bytes;
}

// --- geometry sizing -------------------------------------------------------
// Per-vertex byte stride, summed over the mesh's own vertex buffers. Memoised
// on the geometry because the attribute SET never changes after build (the
// vertex COUNT can, so that is read fresh every call).
function vertexStride(mesh, geom) {
  if (geom && geom.__ashStride !== undefined) return geom.__ashStride;
  // Ask the Geometry, not the mesh: an InstancedMesh has no
  // getVerticesDataKinds of its own, and it is the geometry's buffers we are
  // sizing anyway.
  const src = geom || mesh;
  let stride = 0;
  for (const kind of src.getVerticesDataKinds?.() || []) {
    const vb = src.getVertexBuffer?.(kind);
    // instanced attributes (world0..3 on a clone fan-out) are per-INSTANCE
    // buffers, so multiplying them by the vertex count would be nonsense
    if (vb && !vb.getIsInstanced?.()) stride += vb.getSize(true); // true = bytes
  }
  if (geom) geom.__ashStride = stride;
  return stride;
}

// --- render-target passes --------------------------------------------------
// Triangles and draws one pass submits, straight off its render list. Disabled
// casters are skipped because Babylon skips them too.
function passCost(rtt) {
  const list = rtt.renderList;
  let tris = 0, draws = 0, meshes = 0;
  if (!list) return { tris, draws, meshes };
  for (const m of list) {
    if (!m || m.isDisposed?.() || !m.isEnabled?.() || !m.getTotalIndices) continue;
    const inst = Math.max(1, m.thinInstanceCount || 1);
    tris += (m.getTotalIndices() / 3) * inst;
    // one depth draw per submesh; thin/hardware instances batch into that draw
    draws += m.subMeshes?.length || 1;
    meshes++;
  }
  return { tris, draws, meshes };
}

export class PerfHud {
  constructor(engine, scene, sys) {
    this.engine = engine;
    this.scene = scene;
    this.sys = sys; // { terrain, veg }
    this.si = new SceneInstrumentation(scene);
    this.acc = 0;
    // WeakMap, not Map: a render target that gets disposed (AO can be rebuilt)
    // must not be kept alive by its own measurement bookkeeping.
    this.passSeen = new WeakMap(); // RenderTargetTexture -> observed refresh window
    this.sfx = null;           // resolved lazily, see _audioBytes

    this.el = document.createElement('div');
    this.el.style.cssText = 'position:fixed;top:10px;right:10px;background:#101418d8;'
      + 'color:#9fd49f;font:11px/1.5 ui-monospace,monospace;padding:8px 12px;'
      + 'border:1px solid #2c3542;border-radius:6px;z-index:40;display:none;white-space:pre';
    document.body.appendChild(this.el);
    window.addEventListener('keydown', (e) => {
      if (e.code === 'F3') {
        e.preventDefault();
        this.el.style.display = this.el.style.display === 'none' ? 'block' : 'none';
      }
    });
    window.__perf = this;
  }

  // Every render target that draws scene meshes, discovered rather than listed:
  // the three sun cascades and the survivor's key map arrive through the lights
  // (sunshadow.js gives splits 1 and 2 their own DirectionalLight), the contact
  // prepass and the AO depth pass through scene.customRenderTargets. Discovery
  // is why a seventh pass cannot go unmeasured the way these six did.
  _renderPasses() {
    const out = [];
    for (const light of this.scene.lights) {
      const gens = light.getShadowGenerators?.();
      const list = gens ? [...gens.values()] : [light.getShadowGenerator?.()];
      for (const g of list) {
        const map = g?.getShadowMap?.();
        if (map) out.push({ name: map.name || light.name, rtt: map });
      }
    }
    for (const rtt of this.scene.customRenderTargets || []) {
      if (rtt?.renderList) out.push({ name: rtt.name, rtt });
    }
    return out;
  }

  // How often this pass is ACTUALLY re-rendered, in frames per render.
  //
  // Babylon's refreshRate is "re-render once every N frames", and 0 means
  // MANUAL — which is what every shadow cascade is set to (`map.refreshRate=0`
  // in sunshadow.js's Cascade). Those are driven by resetRefreshCounter() on
  // that file's own phased stagger, and whole splits are switched off by the
  // sun's elevation, so the stride is NOT a constant this file may copy: it
  // belongs to sunshadow.js and would go stale the moment that file is retuned.
  // A manual map therefore has no stride to read at all, so we observe one:
  // count the pass's actual renders over a rolling ~2 s window. Rolling, not
  // cumulative, so a split the sun has just switched off stops being charged
  // for its old stride instead of carrying a two-minute average.
  _passStride(rtt) {
    let p = this.passSeen.get(rtt);
    if (!p) {
      p = { frame0: this.engine.frameId, renders: 0, last: undefined };
      // fires once per real re-render — the only visibility a manual map gives
      rtt.onBeforeRenderObservable.add(() => { p.renders++; });
      this.passSeen.set(rtt, p);
    }
    const frames = this.engine.frameId - p.frame0;
    if (frames >= 120) {
      p.last = p.renders > 0 ? frames / p.renders : Infinity; // Infinity = off
      p.frame0 = this.engine.frameId;
      p.renders = 0;
    }
    if (p.last !== undefined) return p.last;
    if (p.renders > 0) return Math.max(1, frames / p.renders);
    // Nothing observed yet: trust what the owner declared, and read a manual
    // map as every-frame — a pass must never look cheaper than it might be.
    return rtt.refreshRate > 0 ? rtt.refreshRate : 1;
  }

  // Decoded audio. The Sfx buffer cache is only reachable through the game
  // object, and PerfHud is built long before main.js publishes window.__game —
  // hence the lazy resolve. decodeAudioData hands back 32-bit float PCM, one
  // plane per channel, and none of it is visible to performance.memory.
  _audioBytes() {
    if (!this.sfx) this.sfx = window.__game?.audio?.sfx || null;
    const cache = this.sfx?.buffers;
    if (!cache) return 0;
    let bytes = 0;
    for (const bufs of cache.values()) {
      for (const b of bufs) if (b) bytes += b.length * b.numberOfChannels * 4;
    }
    return bytes;
  }

  stats() {
    const { terrain, veg } = this.sys;
    let tilesReady = 0, tilesShown = 0, tilesPending = 0;
    if (terrain) {
      for (const [, t] of terrain.tiles) {
        if (t.state === 'ready') tilesReady++;
        if (t.state === 'pending' || t.state === 'loading' || t.state === 'queued') tilesPending++;
        if (t.mesh?.isEnabled()) tilesShown++;
      }
    }
    let vram = 0, texN = 0;
    for (const t of this.scene.textures) {
      const b = textureBytes(t);
      if (b) { vram += b; texN++; }
    }
    // One walk for both the camera-pass triangles and the geometry tally.
    // Geometry is deduplicated by its Geometry object: scene.meshes lists every
    // InstancedMesh separately, and charging each of them for a shared vertex
    // buffer would invent hundreds of MB that was only ever uploaded once.
    let tris = 0, geo = 0;
    const seenGeo = new Set();
    for (const m of this.scene.meshes) {
      if (!m.getTotalIndices) continue;
      const geom = m.geometry || null;
      const key = geom || m; // keyed on the object: node and geometry ids share a counter
      if (!seenGeo.has(key)) {
        seenGeo.add(key);
        // *2 = 16-bit indices. Babylon promotes to 32-bit above 65 k vertices,
        // so on a big merged mesh this is a floor, not an exact figure.
        geo += m.getTotalVertices() * vertexStride(m, geom) + m.getTotalIndices() * 2;
      }
      if (!m.isEnabled()) continue;
      const t = m.getTotalIndices() / 3;
      tris += t * Math.max(1, m.thinInstanceCount || 1);
    }
    // Per-pass cost, each amortised by the stride it is serviced at.
    const passes = [];
    let passTris = 0, passDraws = 0;
    for (const { name, rtt } of this._renderPasses()) {
      const c = passCost(rtt);
      if (!c.meshes) continue;
      const stride = this._passStride(rtt);
      const live = Number.isFinite(stride);
      // stride null = measured as not being serviced at all right now (a split
      // the sun has switched off). null rather than Infinity so the figure
      // survives the structured clone out of page.evaluate().
      passes.push({
        name, tris: c.tris, draws: c.draws, meshes: c.meshes,
        stride: live ? +stride.toFixed(2) : null, live,
      });
      if (live) { passTris += c.tris / stride; passDraws += c.draws / stride; }
    }
    const audio = this._audioBytes();
    const heapMB = performance.memory
      ? Math.round(performance.memory.usedJSHeapSize / 1e6) : null;
    return {
      fps: +this.engine.getFps().toFixed(1),
      drawCalls: this.si.drawCallsCounter.current,
      activeMeshes: this.scene.getActiveMeshes().length,
      totalMeshes: this.scene.meshes.length,
      tilesReady, tilesShown, tilesPending,
      vegNear: veg?.statNear ?? 0, vegFar: veg?.statFar ?? 0, grass: veg?.statGrass ?? 0,
      trisM: +(tris / 1e6).toFixed(2),
      vramMB: Math.round(vram / 1e6),
      textures: texN,
      heapMB,
      // --- round-9 additions. Raw per-pass counts plus the stride they are
      // serviced at, so a harness can assert either the amortised cost or the
      // worst-case frame where several passes land together.
      passes,
      passCount: passes.length,
      passTrisM: +(passTris / 1e6).toFixed(2),
      passDraws: Math.round(passDraws),
      trisTotalM: +((tris + passTris) / 1e6).toFixed(2),
      geoMB: Math.round(geo / 1e6),
      audioMB: Math.round(audio / 1e6),
      // what the renderer process actually holds, which is the number that
      // decides whether it gets OOM-killed
      totalMB: Math.round((vram + geo + audio) / 1e6) + (heapMB || 0),
    };
  }

  update(dt) {
    if (this.el.style.display === 'none') return;
    this.acc += dt;
    if (this.acc < 0.5) return;
    this.acc = 0;
    const s = this.stats();
    this.el.textContent =
      `FPS         ${s.fps}\n`
      + `draw calls  ${s.drawCalls}  (budget 500)\n`
      + `meshes      ${s.activeMeshes} active / ${s.totalMeshes}\n`
      + `tris        ${s.trisM} M  (budget 3.3 M)\n`
      + `rt passes   ${s.passCount}  +${s.passTrisM} M tris / +${s.passDraws} draws\n`
      + `tris total  ${s.trisTotalM} M\n`
      + `tiles       ${s.tilesShown} shown / ${s.tilesReady} ready / ${s.tilesPending} pending\n`
      + `veg         ${s.vegNear} near + ${s.vegFar} far + ${s.grass} grass\n`
      + `textures    ${s.textures} ≈ ${s.vramMB} MB  (budget 1200)\n`
      + `geometry    ${s.geoMB} MB\n`
      + `audio       ${s.audioMB} MB decoded\n`
      + (s.heapMB ? `JS heap     ${s.heapMB} MB  (budget 900)\n` : '')
      + `total       ${s.totalMB} MB`;
  }
}
